/**
 * TODO: remove when IE11 no longer supported.
 * NOTE: don't add to this or use it unless absolutely required.
 */
export const Browser = {
    ie11: !!(window as any).MSInputMethodContext && !!(document as any).documentMode
}

type Nodes = Element | Element[] | NodeList

/**
 * Referenced (native) events defined here. (Minimizes bundle size.)
 */
export enum DOMEvents {
    CHANGE = 'change',
    click = 'click',
    domContentLoaded = 'DOMContentLoaded',
    load = 'load',
    message = 'message',
    scroll = 'scroll',
    SUBMIT = 'submit',
    VISIBILITY = 'visibilitychange',
    CUSTOM_EVENT = 'custom-event',
    USER_ATTRIBUTE_CHANGE = 'user-attribute-change',
    BEFORE_UNLOAD = 'beforeunload',
    PAGE_HIDE = 'pagehide',
    FOCUS_IN = 'focusin',
    PIANO_METER_EVENT = 'pianometer',
}

export enum DeviceTypes {
    MOBILE = 'mobile',
    TABLET = 'tablet',
    DESKTOP = 'desktop'
}

interface IScriptAttribute {
    key: string,
    value: string
}

/**
 * DOM related helper functions.
 */
export const DOMUtils = {
    /**
     * Referenced attributes defined here. (Minimizes bundle size.)
     */
    attrs: {
        background: 'background',
        class: 'class',
        height: 'height',
        hidden: 'hidden',
        id: 'id',
        src: 'src',
        style: 'style'
    },

    /**
     * Promise for awaiting DOM.
     */
    ready: new Promise<void>((resolve) =>
        document.readyState === 'loading'
            ? document.addEventListener(DOMEvents.domContentLoaded, () => resolve())
            : resolve()),

    /**
     * Referenced tags defined here. (Minimizes bundle size.)
     */
    tagNames: {
        iFrame: 'iframe',
        script: 'script'
    },

    /**
     * Adds styles that were (dynamically) imported as string.
     * NOTE: when styles are known to be required by the requesting module
     * use `import './path/to/styles.scss'` syntax.
     */
    addStyles(css: string): Element {
        const el = document.createElement('style')
        el.innerText = css
        document.head.appendChild(el)
        return el
    },

    /**
     * Hard to debug issue where IE11 sometimes unable to convert via spread (only during readyState interactive?).
     * Probably polyfill conflict (other page assets use babel/polyfill.io etc.).
     * So use this when you need to `<NodeList>.map` (or aren't checking for undefined on `forEach` etc.)
     * @todo remove this when possible and just spread: `[...<NodeList | undefined>]`
     */
    nodes(nodes: Nodes): Node[] {
        return Array.prototype.slice.call(nodes instanceof Element ? [nodes] : nodes)
    },

    setVis(nodes: Nodes, show = true) {
        this.nodes(nodes).forEach((el: Element) => {
            const style = (el.getAttribute(DOMUtils.attrs.style) || '').replace(/;?display:[^;]+/, '')
            el.setAttribute(DOMUtils.attrs.style, `${style}${show ? '' : ';display:none'}`)
            if (show) {
                el.removeAttribute(DOMUtils.attrs.hidden)
            } else {
                el.setAttribute(DOMUtils.attrs.hidden, '')
            }
        })
    },

    /**
     *  Add className to specified element(s)
     * @param el
     * @param className
     */
    addClass(el: Element, className: string) {
        if (el) {
            el.classList.add(className)
        }
    },

    /**
     *  Remove className to specified element(s)
     * @param el
     * @param className
     */
    removeClass(el: Element, className: string) {
        if (el) {
            el.classList.remove(className)
        }
    },

    /**
     * @param el
     * @param className
     */
    checkClass(el: Element, className: string) {
        return el.classList.contains(className)
    },

    /**
     * Replace a current class name to a new class name if exists
     * @param el
     * @param oldClass
     * @param newClass
     */
    replaceClass(el: Element, oldClass: string, newClass: string) {
        DOMUtils.addClass(el, newClass)
        DOMUtils.removeClass(el, oldClass)
    },

    /**
     * Swap between two classes if they exist
     * @param el
     * @param firstClass
     * @param secondClass
     */
    swapClass(el: Element, firstClass: string, secondClass: string) {
        if (DOMUtils.checkClass(el, firstClass)) {
            DOMUtils.removeClass(el, firstClass)
            DOMUtils.addClass(el, secondClass)
        } else if (DOMUtils.checkClass(el, secondClass)) {
            DOMUtils.removeClass(el, secondClass)
            DOMUtils.addClass(el, firstClass)
        }
    },

    /**
     * set, hide and clear elements tab index based on value
     * if no value tabindex is removed, if tabindex >= 0 it sets it otherwise it hides tab
     * @param nodes
     * @param value
     */
    updateTabs(nodes: Nodes, value?: number) {
        this.nodes(nodes).forEach((el: Element) => {
            if (!value) {
                this.removeTabIndex(el)
            } else if (value >= 0) {
                this.setTabIndex(el, value)
            } else {
                this.hideTabIndex(el)
            }
        })
    },

    /**
     * set tab index of an element to value
     * @param el
     * @param value
     */
    setTabIndex(el: Element, value: any) {
        el.setAttribute('tabindex', value)
    },

    /**
     * sets tab index to -1 to hide from default tabbing
     * @param el
     */
    hideTabIndex(el: Element) {
        this.setTabIndex(el, '-1')
    },

    /**
     * check if the id is on the page
     * @param DOM id
     */
    idElementExists(id: string) {
        return document.querySelector(`#${id}`) !== null
    },

    /**
     * remove tab index from an element
     * @param el
     */
    removeTabIndex(el: Element) {
        el.removeAttribute('tabindex')
    },

    /**
     * Manipulates element styles (preserves existing).
     */
    style(nodes: Nodes, key: string, value?: string) {
        this.nodes(nodes).forEach((el: Element) => {
            // get existing styles by key
            // if (el.hasAttribute(DOMUtils.attrs.style)) {
            //     el.getAttribute(DOMUtils.attrs.style).split(';')
            //         .map((kv) => kv.split(':').map((_) => _.trim()))
            //         .forEach(([k, v]) => k && v && (styles[k] = v))
            // }
            const styles = this.getStylesObj(el)
            if (!value) {
                delete styles[key]
            } else {
                styles[key] = value
            }
            this.setStylesObj(el, styles)
        })
    },

    /**
     * Gets all styles from element and creates an object with property as keys
     * @param el
     */
    getStylesObj(el: Element) {

        const styles: { [_: string]: string } = {}
        // get existing styles by key
        if (el.hasAttribute(DOMUtils.attrs.style)) {
            el.getAttribute(DOMUtils.attrs.style).split(';')
                .map((kv) => kv.split(':').map((_) => _.trim()))
                .forEach(([k, v]) => k && v && (styles[k] = v))
        }
        return styles
    },

    /**
     * Set element style attr from object of parameters
     * @param el
     * @param styles
     */
    setStylesObj(el: Element, styles: object) {

        el.setAttribute(
            DOMUtils.attrs.style,
            Object.entries(styles).map(([k, v]) => `${k}:${v}`).join(';'))
    },

    /**
     * Remove an inline style paramter
     * @param el
     * @param key
     */
    removeStyleParam(el: Element, key: string) {
        const styles = this.getStylesObj(el)
        delete styles[key]
        this.setStylesObj(el, styles)
    },

    /**
     * Change all value contents containing old with new values
     * @param el
     * @param oldValue
     * @param newValue
     */
    replaceStyleValues(el: Element, oldValue: string, newValue: string) {
        const styles = this.getStylesObj(el)
        const replace = `${oldValue}`
        const regex = new RegExp(replace, 'g')
        for (const key of Object.keys(styles)) {
            styles[key] = styles[key].replace(regex, newValue)
        }
        this.setStylesObj(el, styles)
    },

    /**
     * Return arr of unique values based on key
     */
    groupKeyValues(arr: any) {
        const groupArr: string[] = []
        arr.forEach((key: any) => {
            if (!groupArr.includes(key)) {
                groupArr.push(key)
            }
        })
        return groupArr
    },

    /**
     * Loads javascript (https). To be used by vendor adapters only.
     *
     * @param src
     * @param attributes custom attributes
     * @param onerror - onerror callback
     */
    loadScript(src: string, attributes?: IScriptAttribute[], onerror?: () => void, onload?: () => void):
        Element {
        const el = document.createElement(DOMUtils.tagNames.script) as HTMLScriptElement
        el.setAttribute('async', '')
        el.src = src.replace(/^(?:https?:)?(.+)$/, 'https:$1')
        // if there are custom attributes to add, include them
        if (attributes) {
            attributes.forEach((attribute: IScriptAttribute) => {
                el.setAttribute(attribute.key, attribute.value)
            })
        }
        if (onerror) {
            el.onerror = onerror
        }
        if (onload) {
            el.onload = onload
        }
        document.head.appendChild(el)
        return el
    },

    /**
     * Check if device is a tablet
     */
    isTablet() {
        const ua = navigator.userAgent
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return true
        }
        if (ua.indexOf('Macintosh') > -1) {
            try {
                document.createEvent('TouchEvent')
                return true
            } catch (e) {
                return false
            }
        }
        return false
    },

    /**
     * Check if device is a mobile
     * https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
     */
    isMobile() {
        const mobile = require('is-mobile')
        return mobile()
    },

    /**
     * Return device type ex..mobile or tablet or desktop
     */
    deviceType() {
        return this.isTablet() ? DeviceTypes.TABLET : this.isMobile() ? DeviceTypes.MOBILE : DeviceTypes.DESKTOP
    },

    /**
     * Returns browser, ex. Chrome
     * https://stackoverflow.com/a/9851769
     * https://stackoverflow.com/a/13348618
     * https://stackoverflow.com/a/20084661
     * https://stackoverflow.com/a/31732310
     */
    browserType() {
        try {
            if (/bot|crawler|spider|crawling/i.test(navigator.userAgent)) {
                return 'Crawler'
            }
            if (!!(window as any).chrome && /google/i.test(navigator.vendor)) {
                return 'Chrome'
            }
            if (navigator.userAgent.match('CriOS')) {
                return 'Chrome'
            }
            if (!!(window as any).StyleMedia) {
                return 'Edge'
            }
            if (!!(window as any).chrome && (!!(window as any).chrome.webstore ||
                !!(window as any).chrome.runtime)) {
                if (navigator.userAgent.indexOf('Edg') !== -1) {
                    return 'Edge'
                }
                return 'Chrome'
            }
            if (/constructor/i.test((window as any).HTMLElement) ||
                ((pushNotification) => pushNotification.toString() === '[object SafariRemoteNotification]')
                (!(window as any).safari ||
                    (typeof (window as any).safari !== 'undefined' &&
                        (window as any).safari.pushNotification))) {
                return 'Safari'
            }
            if (/apple/i.test(navigator.vendor) && !/crios/i.test(navigator.userAgent) &&
                !/fxios/i.test(navigator.userAgent)) {
                return 'Safari'
            }
            if (typeof (window as any).InstallTrigger !== 'undefined') {
                return 'FireFox'
            }
            if ((!!(window as any).opr && !!((window as any).opr as any).addons) ||
                !!(window as any).opera || navigator.userAgent.indexOf(' OPR/') >= 0) {
                return 'Opera'
            }
            if (!!(document as any).documentMode) {
                return 'IE'
            }
            return 'unknown'
        } catch {
            return 'unknown'
        }
    },

    /**
     * Returns browser language, ex. en-US
     * https://stackoverflow.com/a/25603630
     */
    browserLanguage() {
        try {
            return navigator.languages ? navigator.languages[0] :
                (navigator.language || (navigator as any).userLanguage)
        } catch {
            return 'unknown'
        }
    },

    /**
     * Get 'meta' content from Dom
     */
    getMetaContent(val: string) {
        return document.querySelector(`meta[name='${val}']`).getAttribute('content')
    },

    /**
     * Get 'rel' href from Dom node
     */
    getRelUrl(val: string) {
        return document.querySelector(`link[rel='${val}']`).getAttribute('href')
    },

    disableTabs(selectors: string) {
        try {
            const el = document.querySelectorAll<HTMLElement>(selectors)
            DOMUtils.updateTabs(el, -1)
            return el
        } catch { return undefined }
    },

    enableTabs(selectors: string) {
        try {
            const el = document.querySelectorAll<HTMLElement>(selectors)
            DOMUtils.updateTabs(el)
            return el
        } catch { return undefined }
    }
}
